import { Injectable } from '@angular/core'
import { Observable, forkJoin, of } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import { DdcLoggerService } from 'src/app/service/log/ddc-logger.service'
import { DataSearchResult } from '../enums/enums'
import { SearchService } from '../services/search.service'

export interface SearchParams {
  query: string
  type: 'all' | 'word' | 'pair' | 'dictionary' | 'user' | 'note' | 'quiz' | 'figure'
  maxResults?: number
  page?: number
  filters?: {
    languages?: string[]
    articles?: string[]
    partOfSpeeches?: string[]
    categories?: any[]
    tags?: any[]
  }
}

export interface SearchResults {
  words: DataSearchResult[]
  pairs: DataSearchResult[]
  dictionaries: DataSearchResult[]
  users: DataSearchResult[]
  notes: DataSearchResult[]
  quizzes: DataSearchResult[]
  figures: DataSearchResult[]
  all: DataSearchResult[]
}

@Injectable({
  providedIn: 'root',
})
export class SearchCommand {
  constructor(
    private searchService: SearchService,
    private logger: DdcLoggerService,
  ) {}

  /**
   * Execute search based on the provided parameters
   * @param params Search parameters including query, type, and maxResults
   * @returns Observable with search results
   */
  execute(params: SearchParams): Observable<SearchResults> {
    this.logger.info('SearchCommand: execute()', params)

    if (!params.query || params.query.length < 2) {
      return of(this.getEmptyResults())
    }

    const maxResults = params.maxResults || 10

    switch (params.type) {
      case 'all':
        return this.searchAll(params.query, maxResults)
      case 'word':
        return this.searchWords(params.query, maxResults)
      case 'pair':
        return this.searchPairs(params.query, maxResults)
      case 'dictionary':
        return this.searchDictionaries(params.query, maxResults)
      case 'user':
        return this.searchUsers(params.query, maxResults)
      case 'note':
        return this.searchNotes(params.query, maxResults)
      case 'quiz':
        return this.searchQuizzes(params.query, maxResults)
      case 'figure':
        return this.searchFigures(params.query, maxResults)
      default:
        return of(this.getEmptyResults())
    }
  }

  /**
   * Search across all types simultaneously
   */
  private searchAll(query: string, maxResults: number): Observable<SearchResults> {
    this.logger.info('SearchCommand: searchAll()', query, maxResults)

    // Limit results per type for "all" search
    const limitPerType = Math.ceil(maxResults / 4)

    return forkJoin({
      words: this.searchService.searchWords(query, limitPerType).pipe(
        catchError(error => {
          this.logger.error('SearchCommand: searchAll() - words error:', error)
          return of([])
        }),
      ),
      pairs: this.searchService.searchPairs(query, limitPerType).pipe(
        catchError(error => {
          this.logger.error('SearchCommand: searchAll() - pairs error:', error)
          return of([])
        }),
      ),
      dictionaries: this.searchService.searchDictionaries(query, limitPerType).pipe(
        catchError(error => {
          this.logger.error('SearchCommand: searchAll() - dictionaries error:', error)
          return of([])
        }),
      ),
      users: this.searchService.searchUsers(query, limitPerType).pipe(
        catchError(error => {
          this.logger.error('SearchCommand: searchAll() - users error:', error)
          return of([])
        }),
      ),
      notes: this.searchService.searchNotes(query, limitPerType).pipe(
        catchError(error => {
          this.logger.error('SearchCommand: searchAll() - notes error:', error)
          return of([])
        }),
      ),
      quizzes: this.searchService.searchQuizzes(query, limitPerType).pipe(
        catchError(error => {
          this.logger.error('SearchCommand: searchAll() - quizzes error:', error)
          return of([])
        }),
      ),
      figures: this.searchService.searchFigures(query, limitPerType).pipe(
        catchError(error => {
          this.logger.error('SearchCommand: searchAll() - figures error:', error)
          return of([])
        }),
      ),
    }).pipe(
      map(results => {
        // Combine all results for the "all" tab
        const allResults = [
          ...results.words,
          ...results.pairs,
          ...results.dictionaries,
          ...results.users,
          ...results.notes,
          ...results.quizzes,
          ...results.figures,
        ]

        return {
          words: results.words,
          pairs: results.pairs,
          dictionaries: results.dictionaries,
          users: results.users,
          notes: results.notes,
          quizzes: results.quizzes,
          figures: results.figures,
          all: allResults,
        }
      }),
      catchError(error => {
        this.logger.error('SearchCommand: searchAll() - general error:', error)
        return of(this.getEmptyResults())
      }),
    )
  }

  /**
   * Search only words
   */
  private searchWords(query: string, maxResults: number): Observable<SearchResults> {
    return this.searchService.searchWords(query, maxResults).pipe(
      map(words => ({
        words,
        pairs: [] as DataSearchResult[],
        dictionaries: [] as DataSearchResult[],
        users: [] as DataSearchResult[],
        notes: [] as DataSearchResult[],
        quizzes: [] as DataSearchResult[],
        figures: [] as DataSearchResult[],
        all: words,
      })),
      catchError(error => {
        this.logger.error('SearchCommand: searchWords() error:', error)
        return of(this.getEmptyResults())
      }),
    )
  }

  /**
   * Search only pairs
   */
  private searchPairs(query: string, maxResults: number): Observable<SearchResults> {
    return this.searchService.searchPairs(query, maxResults).pipe(
      map(pairs => ({
        words: [] as DataSearchResult[],
        pairs,
        dictionaries: [] as DataSearchResult[],
        users: [] as DataSearchResult[],
        notes: [] as DataSearchResult[],
        quizzes: [] as DataSearchResult[],
        figures: [] as DataSearchResult[],
        all: pairs,
      })),
      catchError(error => {
        this.logger.error('SearchCommand: searchPairs() error:', error)
        return of(this.getEmptyResults())
      }),
    )
  }

  /**
   * Search only dictionaries
   */
  private searchDictionaries(query: string, maxResults: number): Observable<SearchResults> {
    return this.searchService.searchDictionaries(query, maxResults).pipe(
      map(dictionaries => ({
        words: [] as DataSearchResult[],
        pairs: [] as DataSearchResult[],
        dictionaries,
        users: [] as DataSearchResult[],
        notes: [] as DataSearchResult[],
        quizzes: [] as DataSearchResult[],
        figures: [] as DataSearchResult[],
        all: dictionaries,
      })),
      catchError(error => {
        this.logger.error('SearchCommand: searchDictionaries() error:', error)
        return of(this.getEmptyResults())
      }),
    )
  }

  /**
   * Search only users
   */
  private searchUsers(query: string, maxResults: number): Observable<SearchResults> {
    return this.searchService.searchUsers(query, maxResults).pipe(
      map(users => ({
        words: [] as DataSearchResult[],
        pairs: [] as DataSearchResult[],
        dictionaries: [] as DataSearchResult[],
        users,
        notes: [] as DataSearchResult[],
        quizzes: [] as DataSearchResult[],
        figures: [] as DataSearchResult[],
        all: users,
      })),
      catchError(error => {
        this.logger.error('SearchCommand: searchUsers() error:', error)
        return of(this.getEmptyResults())
      }),
    )
  }

  /**
   * Search only notes
   */
  private searchNotes(query: string, maxResults: number): Observable<SearchResults> {
    return this.searchService.searchNotes(query, maxResults).pipe(
      map(notes => ({
        words: [] as DataSearchResult[],
        pairs: [] as DataSearchResult[],
        dictionaries: [] as DataSearchResult[],
        users: [] as DataSearchResult[],
        notes,
        quizzes: [] as DataSearchResult[],
        figures: [] as DataSearchResult[],
        all: notes,
      })),
      catchError(error => {
        this.logger.error('SearchCommand: searchNotes() error:', error)
        return of(this.getEmptyResults())
      }),
    )
  }

  /**
   * Search only quizzes
   */
  private searchQuizzes(query: string, maxResults: number): Observable<SearchResults> {
    return this.searchService.searchQuizzes(query, maxResults).pipe(
      map(quizzes => ({
        words: [] as DataSearchResult[],
        pairs: [] as DataSearchResult[],
        dictionaries: [] as DataSearchResult[],
        users: [] as DataSearchResult[],
        notes: [] as DataSearchResult[],
        quizzes,
        figures: [] as DataSearchResult[],
        all: quizzes,
      })),
      catchError(error => {
        this.logger.error('SearchCommand: searchQuizzes() error:', error)
        return of(this.getEmptyResults())
      }),
    )
  }

  /**
   * Search only figures
   */
  private searchFigures(query: string, maxResults: number): Observable<SearchResults> {
    return this.searchService.searchFigures(query, maxResults).pipe(
      map(figures => ({
        words: [] as DataSearchResult[],
        pairs: [] as DataSearchResult[],
        dictionaries: [] as DataSearchResult[],
        users: [] as DataSearchResult[],
        notes: [] as DataSearchResult[],
        quizzes: [] as DataSearchResult[],
        figures,
        all: figures,
      })),
      catchError(error => {
        this.logger.error('SearchCommand: searchFigures() error:', error)
        return of(this.getEmptyResults())
      }),
    )
  }

  /**
   * Returns empty search results
   */
  private getEmptyResults(): SearchResults {
    return {
      words: [] as DataSearchResult[],
      pairs: [] as DataSearchResult[],
      dictionaries: [] as DataSearchResult[],
      users: [] as DataSearchResult[],
      notes: [] as DataSearchResult[],
      quizzes: [] as DataSearchResult[],
      figures: [] as DataSearchResult[],
      all: [] as DataSearchResult[],
    }
  }

  /**
   * Highlight search terms in results
   */
  highlightSearchTerms(results: DataSearchResult[], searchTerm: string): DataSearchResult[] {
    return results.map(result => ({
      ...result,
      label: result.label.replace(
        new RegExp(searchTerm, 'gi'),
        (match: string) => `<mark>${match}</mark>`,
      ),
    }))
  }
}
